<script setup>
/**
 * FormControlModulestructureDefaultValue
 * Dynamic polymorphism form-control factory wrapper.
 * Inspects structural configuration types on mount to assign custom layout labels,
 * safely hiding itself if a structure style falls outside valid default profiles.
 */
import { computed, onMounted, getCurrentInstance } from 'vue';
import FormControlTypeCheckbox from '../form-control-type/FormControlTypeCheckbox.vue';
import FormControlTypeCurrency from '../form-control-type/FormControlTypeCurrency.vue';
import FormControlTypeText from '../form-control-type/FormControlTypeText.vue';
import FormControlTypeTextarea from '../form-control-type/FormControlTypeTextarea.vue';
import FormControlTypeNumber from "../form-control-type/FormControlTypeNumber.vue";

const props = defineProps({
    // Standard Orchestrator Props
    formId: {
        type: String,
        required: true
    },
    field: {
        type: Object,
        required: true
    },
    currentData: {
        type: Object,
        required: true
    },
    value: {
        type: [String, Number, Array],
        default: null
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Lifecycle ---

onMounted(() => {
    if (!props.field || !props.currentData) return;

    switch (props.currentData.type) {
        case 'currency':
        case 'hidden':
        case 'number':
        case 'text':
        case 'textarea':
            // Properties inherit standard configurations natively
            break;

        case 'date':
            props.field.label = 'Default to current time';
            break;

        case 'select':
            props.field.label = 'Default option';
            break;

        case 'texteditor':
            if (!props.field.id) {
                props.field.id = `editor-${proxy._uid}`;
            }
            break;

        default:
            // Hide control wrapper entirely if an incompatible data type is defined
            props.field.hidden = true;
            break;
    }
});
</script>

<template>
    <div class="form-control-structure-default-value-wrapper">

        <FormControlTypeCurrency
            v-if="currentData.type === 'currency'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeCheckbox
            v-else-if="currentData.type === 'date'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeNumber
            v-else-if="currentData.type === 'number'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeText
            v-else-if="currentData.type === 'select'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeText
            v-else-if="currentData.type === 'text'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeTextarea
            v-else-if="currentData.type === 'textarea'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

        <FormControlTypeTextarea
            v-else-if="currentData.type === 'texteditor'"
            :current-data="currentData"
            :field="field"
            :form-id="formId"
        />

    </div>
</template>